import { Button, Icon, LanguageSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { Node } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import useHasFeature from "@/features/useHasFeature";
import getQASelector from "@/utils/getQASelector";
import { getDynamicData } from "@/view/TiptapArticleBody/extensions/DataProvider";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";
import {
  buttonStyleActive,
  buttonStyleInactive,
} from "./NodeWithTranslation.style";
import useTranslationEventTracking from "./utils/useTranslationEventTracking";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface ParagraphActionProps {
  state: EditorState;
  identifier?: string;
  onClick: () => void;
  node: Node;
}

const TranslateContentButton = ({
  state,
  identifier,
  onClick,
  node,
}: ParagraphActionProps) => {
  const { activeTranslatedItem, isFirstSelection, timeSinceLastSelection } =
    useTranslatorControlsContext();
  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);
  const { levelId } = getDynamicData(state) || {};
  const isActive = activeTranslatedItem === identifier;
  const suffix = isActive ? "on" : "off";
  const qaSelector = getQASelector(TranslateContentButton, identifier, suffix);
  const style = isActive
    ? buttonStyleActive(showLunaAiBranding)
    : buttonStyleInactive(showLunaAiBranding);
  const sanitizedNodeName = node.type.name.replace(/s?_block(_\d*)?$/g, "");
  const metadata = useTranslationEventTracking({
    state,
    node,
    isFirstSelection,
    timeSinceLastSelection,
  });

  return (
    <ButtonWithTrackEvent
      __cssFor={{ root: style }}
      ariaProps={{
        "aria-label": `${isActive ? "close" : "open"} translate drawer for this ${sanitizedNodeName}`,
      }}
      actionName={actionNames.translate}
      dataProps={{ "data-qa-selector": qaSelector }}
      flavor={Button.flavors.incognito}
      objectType={objectTypes.content}
      onClick={onClick}
      size={Button.sizes.sm}
      streamProperties={{
        content_type: "Article",
        description: `translate paragraph ${identifier}`,
        content_id: levelId,
        metadata,
      }}
    >
      <Icon SvgComponent={LanguageSVG} />
    </ButtonWithTrackEvent>
  );
};

export default TranslateContentButton;
